import { z } from 'zod';
import { isoDateSchema, moneyMinorSchema } from '../api';
import { withoutBidiControls } from '../text';
import { TAX_CATEGORIES } from '../countries/types';
import { PACKAGE_PRICING } from './billing';
export const PACKAGE_KINDS = ['invoice_bundle', 'health_checkup', 'ipd_stay', 'session_bundle'];
export const ENROLMENT_PACKAGE_KINDS = ['health_checkup', 'ipd_stay', 'session_bundle'];
export const PATIENT_PACKAGE_STATUS = ['active', 'consumed', 'expired', 'cancelled'];
export const PACKAGE_LINE_ROLES = ['sale', 'usage', 'overrun'];
export const PACKAGE_ALLOWANCE_KINDS = ['health_checkup', 'ipd_stay'];
export const PACKAGE_CANCEL_SETTLEMENT = ['refund', 'keep_credit', 'none'];
export const PACKAGE_VALIDITY_MAX_DAYS = 3650;
export function isEnrolmentKind(kind) {
    return ENROLMENT_PACKAGE_KINDS.includes(kind ?? '');
}
export function packageKindNeedsValidity(kind) {
    return kind === 'health_checkup' || kind === 'session_bundle';
}
export function packageKindHasAllowance(kind) {
    return PACKAGE_ALLOWANCE_KINDS.includes(kind ?? '');
}
export const upsertPackageSchema = z
    .object({
    id: z.string().uuid().optional(),
    code: z.string().trim().toUpperCase().min(2).max(24),
    name: z.preprocess(withoutBidiControls, z.string().trim().min(2).max(200)),
    priceMinor: moneyMinorSchema.min(0),
    inclusionsNote: z.string().max(2000).optional(),
    exclusionsNote: z.string().max(2000).optional(),
    billOverrun: z.boolean().default(true),
    pricing: z.enum(PACKAGE_PRICING).default('manual'),
    nphiesClaimable: z.boolean().default(false),
    kind: z.enum(PACKAGE_KINDS).default('invoice_bundle'),
    validityDays: z.number().int().min(1).max(PACKAGE_VALIDITY_MAX_DAYS).nullable().optional(),
    branchId: z.string().uuid().nullable().optional(),
    taxCategory: z.enum(TAX_CATEGORIES).nullable().optional(),
    allowanceMinor: moneyMinorSchema.min(0).nullable().optional(),
    isActive: z.boolean().default(true),
    items: z.array(z.object({ chargeItemId: z.string().uuid(), qty: z.number().min(0.001).max(10000) })).max(200).default([]),
})
    .superRefine((value, ctx) => {
    if ((value.allowanceMinor ?? 0) > 0 && !packageKindHasAllowance(value.kind)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['allowanceMinor'], message: 'Only a check-up or an inpatient package carries a medicines and consumables allowance' });
    }
    if (!isEnrolmentKind(value.kind))
        return;
    if (value.pricing !== 'manual') {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['pricing'], message: 'A package sold to a patient has a fixed price' });
    }
    if (value.priceMinor <= 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['priceMinor'], message: 'Set the price the patient pays for this package' });
    }
    if (value.items.length === 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['items'], message: 'Add the services the package includes' });
    }
    if (packageKindNeedsValidity(value.kind) && !value.validityDays) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['validityDays'], message: 'Say for how many days the package can be used' });
    }
});
export const sellPatientPackageSchema = z.object({
    packageId: z.string().uuid(),
    patientId: z.string().uuid(),
    branchId: z.string().uuid().optional(),
    appointmentId: z.string().uuid().optional(),
    admissionId: z.string().uuid().optional(),
    performedByUserId: z.string().uuid().nullable().optional(),
});
export const cancelPatientPackageSchema = z.object({
    reason: z.string().trim().min(5).max(400),
    settlement: z.enum(PACKAGE_CANCEL_SETTLEMENT).default('refund'),
});
export const patientPackageQuerySchema = z.object({
    patientId: z.string().uuid().optional(),
    appointmentId: z.string().uuid().optional(),
    admissionId: z.string().uuid().optional(),
    status: z.enum(PATIENT_PACKAGE_STATUS).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.string().uuid().optional(),
});
export const packageCatalogueQuerySchema = z.object({
    kind: z.enum(PACKAGE_KINDS).optional(),
    sellable: z.enum(['0', '1']).optional(),
    active: z.enum(['0', '1']).optional(),
});
export function remainingPackageQty(item) {
    const left = item.includedQty - item.usedQty;
    return left > 1e-6 ? Math.round(left * 1000) / 1000 : 0;
}
export function allowanceLeftMinor(enrolment) {
    return Math.max(0, Math.round(enrolment.allowanceMinor) - Math.max(0, Math.round(enrolment.allowanceUsedMinor)));
}
export const PACKAGE_REVENUE_GROUPINGS = ['package', 'month'];
export const packageRevenueQuerySchema = z.object({
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
    groupBy: z.enum(PACKAGE_REVENUE_GROUPINGS).default('package'),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).max(100_000).default(0),
    export: z.preprocess((value) => (typeof value === 'string' ? ['true', '1', 'yes'].includes(value.toLowerCase()) : value), z.boolean().default(false)),
});
export function packageRefundMinor(params) {
    return Math.max(0, Math.round(params.salePreTaxMinor) - Math.max(0, Math.round(params.consumedValueMinor)));
}
//# sourceMappingURL=packages.js.map